import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { MONTHS } from "../utils/paymentHelpers";
import {
  getAttendanceReport,
  registerFirstEntrance,
  registerExit,
  registerJustification,
  printAttendanceReport,
  printIndividualAttendanceReport,
} from "../services/attendance";

const todayIso = () => new Date().toISOString().split("T")[0];
const firstOfMonthIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

const STATUS_BADGE = {
  COMPLETE: "bg-success-focus text-success-main",
  INCOMPLETE: "bg-warning-focus text-warning-main",
  ABSENT: "bg-danger-focus text-danger-main",
};

// Replaces the old professor-only PresencasProfessoresLayer — see
// menuConfig.js's "presencas" group. routes/attendance.js's GET
// /:sucursalId (without a workerId filter) already unions TEACHER_TABLE and
// WORKER_TABLE into a single per-employee report, so "Colaboradores" here
// means teachers AND funcionários in the very same list, each with their own
// period summary. The page opens straight into that list; clicking a row
// expands it into the day-by-day table used to mark entrances/exits and
// justify absences (same UX pattern as ListaTurmasLayer's roster expansion).
const ListaPresencasColaboradoresLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [startDate, setStartDate] = useState(firstOfMonthIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [search, setSearch] = useState("");

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printingWorkerId, setPrintingWorkerId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busyDate, setBusyDate] = useState(null);

  const [justifyTarget, setJustifyTarget] = useState(null); // { employee, record }
  const [justification, setJustification] = useState("");
  const [justificationType, setJustificationType] = useState("");
  const [savingJustification, setSavingJustification] = useState(false);

  // Ano/Mês are just a convenience to preselect Data Início/Data Fim — the
  // user can still fine-tune both date inputs afterwards. Selecting "Todos
  // os meses" preselects the whole year instead of one month.
  const applyYearMonth = (year, month) => {
    const y = Number(year);
    if (!month) {
      const dec31 = `${y}-12-31`;
      setStartDate(`${y}-01-01`);
      setEndDate(dec31 > todayIso() ? todayIso() : dec31);
      return;
    }
    const m = Number(month);
    const lastDay = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    setStartDate(`${y}-${String(m).padStart(2, "0")}-01`);
    setEndDate(last > todayIso() ? todayIso() : last);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    applyYearMonth(value, selectedMonth);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    applyYearMonth(selectedYear, value);
  };

  const fetchReport = () => {
    setLoading(true);
    getAttendanceReport({ startDate, endDate })
      .then((resp) => setEmployees(resp.data || []))
      .catch(() => toast.error(t("presencaColab.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(fetchReport, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((e) => (e.workerName || "").toLowerCase().includes(term));
  }, [employees, search]);

  // Totals header — sums across every collaborator currently listed below.
  const totals = useMemo(
    () =>
      filteredEmployees.reduce(
        (acc, e) => {
          acc.complete += e.completePresences || 0;
          acc.incomplete += e.incompletePresences || 0;
          acc.justified += e.justifiedAbsences || 0;
          acc.unjustified += e.unjustifiedAbsences || 0;
          return acc;
        },
        { complete: 0, incomplete: 0, justified: 0, unjustified: 0 },
      ),
    [filteredEmployees],
  );

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const resp = await printAttendanceReport({ startDate, endDate, selectedYear, selectedMonth });
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.fileUrl,
          title: t("presencaColab.documento.title", { startDate, endDate }),
          filename: `presencas-colaboradores-${startDate}-a-${endDate}.pdf`,
        },
      });
    } catch (err) {
      toast.error(t("presencaColab.error.print"));
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintIndividual = async (employee) => {
    setPrintingWorkerId(employee.workerId);
    try {
      const resp = await printIndividualAttendanceReport({
        workerId: employee.workerId,
        startDate,
        endDate,
        selectedYear,
        selectedMonth,
      });
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.fileUrl,
          title: t("presencaColab.documento.individualTitle", { name: employee.workerName, startDate, endDate }),
          filename: `presencas-${employee.workerName}-${startDate}-a-${endDate}.pdf`,
        },
      });
    } catch (err) {
      toast.error(t("presencaColab.error.printIndividual"));
    } finally {
      setPrintingWorkerId(null);
    }
  };

  const toggleExpand = (workerId) => {
    setExpandedId((prev) => (prev === workerId ? null : workerId));
  };

  const handleRegisterEntrance = async (employee, date) => {
    setBusyDate(date);
    try {
      await registerFirstEntrance({ workerId: employee.workerId, workerName: employee.workerName });
      toast.success(t("presencaColab.success.entrance"));
      fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("presencaColab.error.entrance"));
    } finally {
      setBusyDate(null);
    }
  };

  const handleRegisterExit = async (record) => {
    setBusyDate(record.date);
    try {
      await registerExit(record.id);
      toast.success(t("presencaColab.success.exit"));
      fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("presencaColab.error.exit"));
    } finally {
      setBusyDate(null);
    }
  };

  const openJustify = (employee, record) => {
    setJustifyTarget({ employee, record });
    setJustification("");
    setJustificationType("");
  };

  const handleSaveJustification = async () => {
    if (!justification) {
      toast.error(t("presencaColab.error.justificationRequired"));
      return;
    }
    const { employee, record } = justifyTarget;
    setSavingJustification(true);
    try {
      await registerJustification({
        id: record.id || null,
        workerId: employee.workerId,
        workerName: employee.workerName,
        date: record.date,
        justification,
        justificationType: justificationType || "OUTRO",
      });
      toast.success(t("presencaColab.success.justified"));
      setJustifyTarget(null);
      fetchReport();
    } catch (err) {
      toast.error(t("presencaColab.error.justify"));
    } finally {
      setSavingJustification(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='row gy-3 align-items-end'>
              <div className='col-sm-3'>
                <label className='form-label'>{t("presencaColab.field.year")}</label>
                <select className='form-select' value={selectedYear} onChange={(e) => handleYearChange(e.target.value)}>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("presencaColab.field.month")}</label>
                <select className='form-select' value={selectedMonth} onChange={(e) => handleMonthChange(e.target.value)}>
                  <option value=''>{t("presencaColab.field.month.all")}</option>
                  {MONTHS.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.desc}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-6'>
                <label className='form-label'>{t("presencaColab.field.search")}</label>
                <div className='icon-field'>
                  <span className='icon'>
                    <Icon icon='mdi:magnify' />
                  </span>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={t("presencaColab.field.search.placeholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-sm-4'>
                <label className='form-label'>{t("presencaColab.field.startDate")}</label>
                <div className='icon-field'>
                  <span className='icon'>
                    <Icon icon='solar:calendar-outline' />
                  </span>
                  <input type='date' className='form-control' value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
              </div>
              <div className='col-sm-4'>
                <label className='form-label'>{t("presencaColab.field.endDate")}</label>
                <div className='icon-field'>
                  <span className='icon'>
                    <Icon icon='solar:calendar-outline' />
                  </span>
                  <input type='date' className='form-control' value={endDate} max={todayIso()} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>
              <div className='col-sm-auto'>
                <button
                  type='button'
                  className='btn border border-primary-600 text-primary-600 d-flex align-items-center gap-2'
                  onClick={handlePrint}
                  disabled={printing || loading || filteredEmployees.length === 0}
                >
                  <Icon icon='solar:printer-linear' />
                  {printing ? t("global.loading") : t("presencaColab.action.print")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='row gy-3'>
          <div className='col-sm-3'>
            <div className='p-16 radius-8 bg-success-focus text-center'>
              <span className='text-secondary-light text-sm d-block'>{t("presencaColab.col.complete")}</span>
              <strong className='text-success-main text-xl'>{totals.complete}</strong>
            </div>
          </div>
          <div className='col-sm-3'>
            <div className='p-16 radius-8 bg-warning-focus text-center'>
              <span className='text-secondary-light text-sm d-block'>{t("presencaColab.col.incomplete")}</span>
              <strong className='text-warning-main text-xl'>{totals.incomplete}</strong>
            </div>
          </div>
          <div className='col-sm-3'>
            <div className='p-16 radius-8 bg-primary-50 text-center'>
              <span className='text-secondary-light text-sm d-block'>{t("presencaColab.col.justified")}</span>
              <strong className='text-primary-600 text-xl'>{totals.justified}</strong>
            </div>
          </div>
          <div className='col-sm-3'>
            <div className='p-16 radius-8 bg-danger-focus text-center'>
              <span className='text-secondary-light text-sm d-block'>{t("presencaColab.col.unjustified")}</span>
              <strong className='text-danger-main text-xl'>{totals.unjustified}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : filteredEmployees.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("presencaColab.hint.noEmployees")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th>{t("presencaColab.col.employee")}</th>
                      <th>{t("presencaColab.col.complete")}</th>
                      <th>{t("presencaColab.col.incomplete")}</th>
                      <th>{t("presencaColab.col.justified")}</th>
                      <th>{t("presencaColab.col.unjustified")}</th>
                      <th className='text-end'>{t("presencaColab.col.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((employee) => {
                      const isExpanded = expandedId === employee.workerId;
                      const records = (employee.attendanceDetails || [])
                        .slice()
                        .sort((a, b) => (a.date < b.date ? 1 : -1));
                      return (
                        <React.Fragment key={employee.workerId}>
                          <tr
                            role='button'
                            onClick={() => toggleExpand(employee.workerId)}
                            className={isExpanded ? "bg-neutral-50" : ""}
                          >
                            <td>
                              <Icon
                                icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                                className='text-secondary-light text-xl'
                              />
                            </td>
                            <td className='fw-semibold text-primary-light'>{employee.workerName}</td>
                            <td><span className='badge bg-success-focus text-success-main'>{employee.completePresences}</span></td>
                            <td><span className='badge bg-warning-focus text-warning-main'>{employee.incompletePresences}</span></td>
                            <td><span className='badge bg-primary-50 text-primary-600'>{employee.justifiedAbsences}</span></td>
                            <td><span className='badge bg-danger-focus text-danger-main'>{employee.unjustifiedAbsences}</span></td>
                            <td className='text-end' onClick={(e) => e.stopPropagation()}>
                              <button
                                type='button'
                                className='btn btn-sm border border-primary-600 text-primary-600 d-inline-flex align-items-center gap-2'
                                title={t("presencaColab.action.printIndividual.title", { name: employee.workerName })}
                                disabled={printingWorkerId === employee.workerId}
                                onClick={() => handlePrintIndividual(employee)}
                              >
                                <Icon icon='solar:printer-linear' />
                                {printingWorkerId === employee.workerId ? t("global.loading") : t("presencaColab.action.printIndividual")}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} className='p-0'>
                                <div className='p-24 bg-neutral-25 border-top'>
                                  {records.length === 0 ? (
                                    <div className='alert alert-info mb-0'>{t("presencaColab.hint.noRecords")}</div>
                                  ) : (
                                    <div className='table-responsive'>
                                      <table className='table bordered-table mb-0'>
                                        <thead>
                                          <tr>
                                            <th>{t("presenca.field.date")}</th>
                                            <th>{t("presencaColab.col.entry")}</th>
                                            <th>{t("presencaColab.col.exit")}</th>
                                            <th>{t("presenca.col.status")}</th>
                                            <th>{t("presenca.col.justification")}</th>
                                            <th>{t("turma.col.actions")}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {records.map((record) => {
                                            const isToday = record.date === todayIso();
                                            return (
                                              <tr key={record.date}>
                                                <td>{record.date}</td>
                                                <td>{record.entryTime || "-"}</td>
                                                <td>{record.exitTime || "-"}</td>
                                                <td>
                                                  <span className={`badge ${STATUS_BADGE[record.status] || "bg-neutral-200 text-neutral-600"}`}>
                                                    {t(`presencaColab.status.${record.status}`) || record.status}
                                                  </span>
                                                </td>
                                                <td>{record.justified ? record.justification || "-" : "-"}</td>
                                                <td>
                                                  <div className='d-flex flex-wrap gap-2'>
                                                    {isToday && !record.id && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-success-600 btn-sm'
                                                        disabled={busyDate === record.date}
                                                        onClick={() => handleRegisterEntrance(employee, record.date)}
                                                      >
                                                        <Icon icon='mdi:login' /> {t("presencaColab.action.entrance")}
                                                      </button>
                                                    )}
                                                    {record.status === "INCOMPLETE" && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-primary-600 btn-sm'
                                                        disabled={busyDate === record.date}
                                                        onClick={() => handleRegisterExit(record)}
                                                      >
                                                        <Icon icon='mdi:logout' /> {t("presencaColab.action.exit")}
                                                      </button>
                                                    )}
                                                    {record.status === "ABSENT" && !record.justified && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-warning-600 btn-sm'
                                                        onClick={() => openJustify(employee, record)}
                                                      >
                                                        <Icon icon='mdi:file-document-edit-outline' /> {t("presencaColab.action.justify")}
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {justifyTarget && (
        <div className='modal show d-block' style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h6 className='mb-0'>{t("presencaColab.justifyModal.title", { date: justifyTarget.record.date })}</h6>
                <button type='button' className='btn-close' onClick={() => setJustifyTarget(null)} />
              </div>
              <div className='modal-body'>
                <div className='mb-16'>
                  <label className='form-label'>{t("presencaColab.field.justificationType")}</label>
                  <div className='icon-field'>
                    <span className='icon'>
                      <Icon icon='solar:pen-2-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      value={justificationType}
                      onChange={(e) => setJustificationType(e.target.value)}
                      placeholder='ILLNESS, OUTRO...'
                    />
                  </div>
                </div>
                <div>
                  <label className='form-label'>{t("presencaColab.field.justification")}</label>
                  <textarea className='form-control' rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} />
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-outline-secondary' onClick={() => setJustifyTarget(null)}>
                  {t("global.cancel")}
                </button>
                <button type='button' className='btn btn-primary-600' onClick={handleSaveJustification} disabled={savingJustification}>
                  {savingJustification ? t("global.loading") : (<><i className='ri-save-line' /> {t("global.save")}</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaPresencasColaboradoresLayer;
